<script lang="ts">
  import SEO from '$lib/components/SEO.svelte';

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': 'https://bleunk.com/about#webpage',
        url: 'https://bleunk.com/about',
        name: 'About Bleunk — AI Automation Studio',
        description: 'A global senior technical studio helping SMEs automate their business operations with AI force-multipliers.',
        isPartOf: { '@id': 'https://bleunk.com/#website' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://bleunk.com' },
          { '@type': 'ListItem', position: 2, name: 'About', item: 'https://bleunk.com/about' },
        ],
      },
    ],
  };
</script>

<SEO 
  title="About — Bleunk AI Automation Studio"
  description="A global senior technical studio helping SMEs automate their business operations with AI force-multipliers."
  canonical="https://bleunk.com/about"
/>

<svelte:head>
  <meta name="keywords" content="about Bleunk, AI automation team, global business automation studio, Bleunk Technologies" />
  {@html `<script type="application/ld+json">${JSON.stringify(schemaOrg)}</script>`}
</svelte:head>

<section class="px-4 pt-40 pb-24">
  <div class="max-w-4xl mx-auto">

    <!-- Hero -->
    <div class="text-center mb-16 max-w-3xl mx-auto">
      <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dim-8 bg-dim-3 text-dim-50 text-xs font-medium mb-6">
        The Studio
      </div>
      <h1 class="font-display font-bold text-5xl sm:text-6xl leading-tight mb-6">
        Small team. <span class="gradient-accent-text">Unfair leverage.</span>
      </h1>
      <p class="text-dim-60 text-lg leading-relaxed">
        We're a senior technical studio obsessed with operational velocity. We don't just "automate" — we build the force-multipliers that allow high-growth teams to scale their output without bloating their headcount or burning out their best talent.
      </p>
    </div>

    <!-- Team photo -->
    <div class="rounded-3xl overflow-hidden border border-dim-8 shadow-2xl mb-16 relative group">
      <img
        src="/team-working.webp"
        alt="Bleunk senior team architecting AI business systems"
        width="1200"
        height="800"
        loading="eager"
        decoding="async"
        class="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
      />
      <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none"></div>
    </div>

    <!-- Prose block -->
    <div class="rounded-2xl border border-dim-6 bg-card px-8 sm:px-10 py-10 mb-16 max-w-3xl mx-auto relative overflow-hidden">
      <div aria-hidden="true" class="absolute -top-24 -right-24 w-48 h-48 bg-[#C9A87C]/5 rounded-full blur-3xl"></div>
      <p class="text-dim-70 text-base leading-relaxed relative z-10 font-medium">
        We are senior architects, not account managers. At Bleunk, you bypass the junior-led agency bloat. You work directly with the builders who map your workflows, write the code, and deploy the agents. We believe your best people are too smart for the grind — so we build the systems that set them free.
      </p>
    </div>

    <!-- Values strip -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-16 max-w-3xl mx-auto">
      {#each [
        { label: 'Senior Execution', detail: 'Every automation is architected by veterans. No juniors, no learning on your time.', accent: '#C9A87C' },
        { label: 'Fixed Timelines', detail: 'We commit to a delivery date before you pay. No scope creep, no endless sprints.', accent: '#8E9BAF' },
        { label: 'Direct Protocol', detail: 'No middlemen. You have a direct line to the technical team building your future.', accent: '#7A9E90' },
      ] as value}
        <div class="rounded-xl border border-dim-6 bg-dim-2 p-6 hover:border-[#C9A87C]/20 transition-colors">
          <div class="w-6 h-0.5 rounded-full mb-4" style="background-color: {value.accent}"></div>
          <p class="font-display font-bold text-base text-content mb-2">{value.label}</p>
          <p class="text-dim-50 text-xs leading-relaxed">{value.detail}</p>
        </div>
      {/each}
    </div>

    <!-- Global Infrastructure -->
    <div class="mb-16">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 class="font-display font-bold text-3xl mb-6">Global infrastructure. <br/><span class="text-gold">Localized service.</span></h2>
          <p class="text-dim-50 text-sm leading-relaxed mb-6">
            We operate as a distributed studio with a strategic operations hub in Bonifacio Global City (BGC), Philippines. This hub allows us to offer 24/7 technical coverage and high-velocity output while maintaining the standards of a premium boutique studio.
          </p>
          <div class="space-y-4">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-full bg-[#7A9E90]/10 border border-[#7A9E90]/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-[#7A9E90]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <span class="text-xs text-dim-60 font-medium">24/7 Global Technical Coverage</span>
            </div>
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-full bg-[#8E9BAF]/10 border border-[#8E9BAF]/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-[#8E9BAF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <span class="text-xs text-dim-60 font-medium">Clients across 4+ Continents</span>
            </div>
          </div>
        </div>
        <div class="relative rounded-2xl overflow-hidden border border-dim-8 bg-card aspect-video shadow-xl">
           <img
            src="/hero-bgc.webp"
            alt="Strategic operations hub"
            class="w-full h-full object-cover opacity-60 grayscale hover:grayscale-0 transition-all duration-700"
          />
          <div class="absolute inset-0 flex items-center justify-center">
             <div class="px-4 py-2 rounded-xl bg-void/80 border border-white/10 backdrop-blur-md text-[10px] font-mono text-white tracking-widest uppercase">
                Active Node: BGC_MANILA
             </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="text-center rounded-2xl border border-[#C9A87C]/20 bg-[#C9A87C]/5 p-10 max-w-3xl mx-auto">
      <h2 class="font-display font-bold text-2xl mb-3">Ready to automate?</h2>
      <p class="text-dim-50 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
        Tell us what tasks are eating your team's time. We'll map out the automation
        and send you a fixed-price proposal within 48 hours.
      </p>
      <a
        href="/#contact"
        class="inline-block px-7 py-3.5 rounded-2xl text-sm font-semibold
               bg-[#C9A87C] hover:bg-[#D4B896] text-[#08080E]
               transition-all duration-200 shadow-lg shadow-[#C9A87C]/20
               hover:shadow-[#C9A87C]/35 hover:-translate-y-0.5
               focus-visible:outline-none focus-visible:ring-2
               focus-visible:ring-[#C9A87C]/60 focus-visible:ring-offset-2"
      >
        Start a project →
      </a>
    </div>

  </div>
</section>
